export interface ChatFileRef { id: string; name: string }
export interface ChatMeta { intent?: string; scopeLabel?: string; chips?: { kind: string; label: string }[]; files?: ChatFileRef[]; model?: string; scene?: { name: string; instruction?: string } }
export interface ChatMessage {
  id: string
  session_id: string
  user_text: string
  assistant_text: string | null
  status: 'queued' | 'running' | 'ready' | 'failed' | 'cancelled' | 'interrupted'
  error_code?: string | null
  model?: string | null
  meta?: ChatMeta | null
  /** 服务端按 meta.files 水合的云文件详情（限本人文件），用于图片直显与下载预览 */
  attachments?: import('./cloud-files').CloudFile[]
  created_at: string
  parent_id?: string | null
  feedback?: 'up' | 'down' | null
  branch_index?: number
  branch_count?: number
  prev_branch_id?: string | null
  next_branch_id?: string | null
  /** 本条回答实际调用过的工具名（去重），由后端按 chat.tool_call 聚合 */
  tool_names?: string[]
}
export interface ChatThread { messages: ChatMessage[]; pathIds: string[] | null; headId: string | null }
/** 回答处理的实时阶段（服务端内存态，瞬时 UI 信号）：小人念叨用，非业务事实 */
export interface ChatPhase { stage: 'context' | 'think' | 'tool' | 'write'; round?: number; tool?: string }
export interface ChatStreamEvent { event: string; data: { id?: string; status?: ChatMessage['status']; text?: string; error_code?: string | null; message?: string; phase?: ChatPhase | null } }
export interface ChatSessionRow {
  id: string
  title: string | null
  created_at: string
  updated_at: string
  message_count?: number
  pinned_at?: string | null
  archived_at?: string | null
  /** 会话健康：ok 正常 / blocked 系统阻断 / attention 需再处理；由最近一条消息推导 */
  health?: 'ok' | 'blocked' | 'attention' | null
}
export interface ChatRuntime {
  enabled: boolean
  provider: string
  model: string
  label: string
  historyTurns: number
}
/** 会话级行为规则：本人在当前对话中约定的自然语言原则，并入该会话后续回答的系统提示 */
export interface ChatSessionRule { id: string; text: string; createdAt: string }
export interface ChatModelItem { id: string; name: string }
/** 可选模型列表：live=实时取自腾讯云；fallback=列表暂不可用只给环境默认；none=未配置密钥 */
export interface ChatModels {
  enabled: boolean
  provider: string
  defaultModel: string
  source: 'live' | 'fallback' | 'none'
  models: ChatModelItem[]
}

async function request<T>(path: string, body?: unknown, method?: 'POST' | 'PATCH' | 'DELETE'): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), 20000)
  try {
    const r = await fetch('/api/chat' + path, {
      method: method || (body === undefined ? 'GET' : 'POST'),
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
      cache: 'no-store',
    })
    const data = await r.json()
    if (!r.ok || data.code !== 0) throw Error(data.message || '聊天请求未完成')
    return data.data as T
  } finally { clearTimeout(timer) }
}

export const chatApi = {
  list: (offset = 0) => request<ChatMessage[]>('/messages?offset=' + offset),
  messages: (sessionId: string, before?: string, beforeId?: string) => request<ChatMessage[]>('/messages?sessionId=' + encodeURIComponent(sessionId) + (before ? '&before=' + encodeURIComponent(before) : '') + (beforeId ? '&beforeId=' + encodeURIComponent(beforeId) : '')),
  thread: (sessionId: string, before?: string, beforeId?: string) => request<ChatThread>('/sessions/' + encodeURIComponent(sessionId) + '/thread' + ((before || beforeId) ? '?before=' + encodeURIComponent(before || '') + '&beforeId=' + encodeURIComponent(beforeId || '') : '')),
  activate: (sessionId: string, messageId: string, expectedHeadId: string | null) => request<{ headId: string }>('/sessions/' + encodeURIComponent(sessionId) + '/head', { messageId, expectedHeadId }),
  feedback: (id: string, vote: 'up' | 'down' | null) => request<{ messageId: string; vote: 'up' | 'down' | null }>('/messages/' + encodeURIComponent(id) + '/feedback', { vote }),
  sessions: (q = '', offset = 0) => request<ChatSessionRow[]>('/sessions?offset=' + offset + (q ? '&q=' + encodeURIComponent(q) : '')),
  rename: (id: string, title: string) => request<ChatSessionRow>('/sessions/' + encodeURIComponent(id), { title }, 'PATCH'),
  pin: (id: string, pinned: boolean) => request<ChatSessionRow>('/sessions/' + encodeURIComponent(id) + '/pin', { pinned }),
  archive: (id: string, archived: boolean) => request<ChatSessionRow>('/sessions/' + encodeURIComponent(id) + '/archive', { archived }),
  remove: (id: string) => request<{ deleted: true }>('/sessions/' + encodeURIComponent(id) + '/delete', {}),
  send: (sessionId: string, text: string, requestKey: string, meta?: ChatMeta, title?: string) => request<ChatMessage>('/messages', { sessionId, text, requestKey, ...(meta && Object.keys(meta).length ? { meta } : {}), ...(title ? { title } : {}) }),
  cancel: (id: string) => request('/messages/' + encodeURIComponent(id) + '/cancel', {}),
  retry: (id: string) => request<ChatMessage>('/messages/' + encodeURIComponent(id) + '/retry', {}),
  edit: (id: string, text: string, meta?: ChatMeta) => request<ChatMessage>('/messages/' + encodeURIComponent(id) + '/edit', meta === undefined ? { text } : { text, meta }),
  runtime: () => request<ChatRuntime>('/runtime'),
  models: () => request<ChatModels>('/models'),
  /* 会话级行为规则：本人在当前对话中约定的原则，后续回答自动遵循 */
  rules: (sessionId: string) => request<ChatSessionRule[]>('/sessions/' + encodeURIComponent(sessionId) + '/rules'),
  addRule: (sessionId: string, text: string) => request<ChatSessionRule>('/sessions/' + encodeURIComponent(sessionId) + '/rules', { text }),
  removeRule: (sessionId: string, ruleId: string) => request<{ deleted: true }>('/sessions/' + encodeURIComponent(sessionId) + '/rules/' + encodeURIComponent(ruleId), undefined, 'DELETE'),
  async watch(id: string, onEvent: (event: ChatStreamEvent) => void, signal: AbortSignal) {
    const response = await fetch('/api/chat/messages/' + encodeURIComponent(id) + '/stream', { credentials: 'same-origin', cache: 'no-store', signal, headers: { Accept: 'text/event-stream' } })
    if (!response.ok || !response.body) {
      const data = await response.json().catch(() => null)
      throw Error(data?.message || '回答流没有连上')
    }
    await readChatStream(response.body, onEvent)
  },
}

export async function readChatStream(body: ReadableStream<Uint8Array>, onEvent: (event: ChatStreamEvent) => void) {
  const reader = body.getReader()
  const decoder = new TextDecoder()
  let pending = ''
  while (true) {
    const chunk = await reader.read()
    if (chunk.done) break
    pending += decoder.decode(chunk.value, { stream: true })
    const parts = pending.split('\n\n')
    pending = parts.pop() || ''
    for (const part of parts) emitChatEvent(part, onEvent)
  }
  if (pending.trim()) emitChatEvent(pending, onEvent)
}

function emitChatEvent(part: string, onEvent: (event: ChatStreamEvent) => void) {
  let event = 'message'
  const data: string[] = []
  for (const line of part.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) data.push(line.slice(5).trim())
  }
  if (!data.length) return
  onEvent({ event, data: JSON.parse(data.join('\n')) })
}
